import { ApolloClient, HttpLink, InMemoryCache, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getSession, signOut } from 'next-auth/react';
import {
  ORG_STORAGE_KEY,
  clearContractorSessionState,
  clearSelectedOrganization,
  hasAttemptedOrgRecovery,
  markOrgRecoveryAttempted,
  getEnvironment,
  getGraphqlUrl,
  getStorageItem,
  isSessionRevokedFlag,
  setSessionRevoked,
  setSessionRevocationInProgress,
  resetSessionRevokedFlags,
} from './api-config';

declare module 'next-auth' {
  interface User {
    access_token?: string;
    refresh_token?: string;
  }
}

// Create HTTP link
const httpLink = new HttpLink({
  uri: getGraphqlUrl(),
});

// Auth link - adds headers to requests
const authLink = setContext(async (_, { headers }) => {
  const session = await getSession();
  const accessToken = session?.user?.access_token;

  // Use URL path as source of truth so header always matches the page (avoids
  // sandbox session/localStorage sending sandbox when user is on /billing/... without /sandbox).
  let environment: string | undefined;
  if (typeof window !== 'undefined') {
    environment = window.location.pathname.includes('/sandbox') ? 'sandbox' : 'production';
  }
  if (!environment) {
    environment = (session?.user as any)?.environment || getEnvironment() || 'production';
  }
  
  const organization = getStorageItem(ORG_STORAGE_KEY);

  return {
    headers: {
      ...headers,
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      'Ten99Policy-Environment': environment,
      'Ten99Policy-Organization': organization,
    },
  };
});

    // Session revocation handler - properly logout user
//
// `reason` becomes ?message= on the sign-in screen, which decides what the user
// is told. Two different things route through here: a revoked session, and
// contractor portal access that was taken away since the token was minted.
// Telling someone their session expired when their access changed sends them
// round the loop of signing in again and hitting the same wall, so callers that
// know better say so. The authenticated layout already uses
// portal_access_required for this same case.
const handleSessionRevoked = async (reason = 'session_revoked') => {
  if (typeof window !== 'undefined') {
    // Set flags to prevent duplicate logout attempts
    setSessionRevoked(true);
    setSessionRevocationInProgress(true);

    // A forced sign-out has to forget the selected profile for the same reason
    // a voluntary one does: the value outlives the session, so whoever signs in
    // next on this browser inherits a selection that may not be theirs. Doing it
    // here rather than at each call site covers every path into a forced logout
    // — session revoked, access revoked, and a 401 from the network.
    clearContractorSessionState();

    try {
      // Determine environment for redirect
      const environment = getEnvironment();
      const isSandbox = environment === 'sandbox' || window.location.pathname.startsWith('/sandbox');
      const signinPath = isSandbox ? '/sandbox/signin' : '/signin';

      // The key fix: Use redirect: false to prevent NextAuth from handling redirect
      // This ensures NextAuth clears cookies properly before our redirect
      await signOut({ redirect: false });

      // Small delay to ensure cookies are cleared by NextAuth
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Now redirect to signin with message
      window.location.href = `${signinPath}?message=${reason}`;
    } catch (error) {
      console.error('Error during session revocation logout:', error);
      // Fallback: force redirect even if signOut fails
      const isSandbox = window.location.pathname.startsWith('/sandbox');
      const signinPath = isSandbox ? '/sandbox/signin' : '/signin';
      window.location.href = `${signinPath}?message=${reason}`;
    }
  }
};

// Dispatched on window whenever any operation comes back OTP_REQUIRED: the
// session has not verified its second factor, and the API refuses almost
// everything until it does. TwoFactorGate listens and shows the code prompt,
// wherever the user is -- including mid-session, which is where every session
// that predates the server-side check lands the first time it is used.
export const OTP_REQUIRED_EVENT = 'ten99policy:otp-required';

// Error link - handles GraphQL errors
const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    // Don't process errors if session already revoked
    if (isSessionRevokedFlag()) {
      return;
    }

    // Handle GraphQL errors
    if (graphQLErrors) {
      for (const error of graphQLErrors) {
        const { message, extensions } = error;

        // Check for session revoked errors - ONLY these specific errors
        if (
          message === 'FORCE_LOGOUT_SESSION_REVOKED' ||
          extensions?.status === 'FORCE_LOGOUT_SESSION_REVOKED' ||
          extensions?.status === 'SESSION_REVOKED' ||
          extensions?.code === 'SESSION_INVALID'
        ) {
          handleSessionRevoked();
          return;
        }

        // Not a sign-out: the session is valid, it just owes a code.
        if (extensions?.status === 'OTP_REQUIRED') {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event(OTP_REQUIRED_EVENT));
          }
          return;
        }

        // The user holds NO active contractor access at all — it was revoked
        // since this token was minted. `platforms` lives in the NextAuth JWT
        // (30-day maxAge, only refreshed on an explicit update), so the token
        // outlives the grant it describes: they keep passing the client-side
        // gate while every query fails. Nothing they can select fixes it, so
        // sign them out rather than leaving them on a portal that cannot load.
        // Signing in again re-mints the token against current access.
        //
        // Matched on the message too so this works against an API that predates
        // the distinct status (it previously reported CONTRACTOR_ACCESS_REQUIRED
        // for both cases).
        if (
          extensions?.status === 'CONTRACTOR_ACCESS_REVOKED' ||
          message === 'No contractor portal access'
        ) {
          console.error(
            'GraphQL Error: contractor portal access revoked; signing out'
          );
          // handleSessionRevoked clears the stored selection for us. Their
          // access changed rather than their session expiring, so send the
          // message that says so and points them at a new login link.
          handleSessionRevoked('portal_access_required');
          return;
        }

        // The backend could not resolve a contractor for the SELECTED org, but
        // the user does have access to something. Usually the stored selection
        // points at a profile whose access was revoked, or belongs to a
        // different account on a shared browser. Every data query fails, and
        // because this used to be swallowed here the user just saw an empty
        // page — on every reload, on every device, with nothing to act on.
        //
        // Drop the poisoned selection so the org switcher re-resolves to a
        // profile the user can actually reach on the next render, instead of
        // replaying the same failure forever. Do NOT sign out: they have a
        // working profile to fall back to.
        if (extensions?.status === 'CONTRACTOR_ACCESS_REQUIRED') {
          clearSelectedOrganization();
          console.error(
            'GraphQL Error: no contractor profile for the selected organization; cleared the stored selection'
          );

          // Clearing the selection is not enough on its own: this fires from a
          // link, so nothing re-renders the switcher or retries the queries
          // that just failed, and the user sits on the same broken page until
          // they reload by hand. Reload for them so the switcher re-resolves
          // against a profile they can reach.
          //
          // Once per tab only. If the org we fall back to ALSO fails, a second
          // reload would clear, reload, and fail again forever — worse than the
          // stale page. Stop after one attempt and let the errors show.
          if (typeof window !== 'undefined' && !hasAttemptedOrgRecovery()) {
            markOrgRecoveryAttempted();
            window.location.reload();
          }
          return;
        }

        // Log GraphQL errors in development only (excluding session revocation)
        if (process.env.NODE_ENV === 'development') {
          console.error('GraphQL Error:', message);
        }
      }
    }

    // Handle network errors
    if (
      networkError &&
      'statusCode' in networkError &&
      networkError.statusCode === 401
    ) {
      handleSessionRevoked();
      return;
    }
  }
);

// Create Apollo client
const client = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
});

// Re-export reset function for debugging
export { resetSessionRevokedFlags as resetSessionRevokedFlag } from './api-config';

// Expose for debugging
if (typeof window !== 'undefined') {
  (
    window as unknown as {
      resetSessionRevokedFlag: typeof resetSessionRevokedFlags;
    }
  ).resetSessionRevokedFlag = resetSessionRevokedFlags;
}

export default client;
